import { useEffect, useState } from "react";

// Components
import FormCard from "../../Common/Childs/Structure/FormCard";
import Table from "./Childs/Table";
import Description from "../../Common/Childs/Structure/Description";
import Title from "../../Common/Childs/Structure/Title";
import Form from "./Childs/Form";
import Delete from "./Delete";
import { useGlobal } from "../../../context/GlobalContextProvider";
import { useWallet } from "../../../context/WalletContextProvider";

// Configuration
import plus from "../../../assets/icons/plus-solid.svg";

const MAX_PRESETS = 5;

/**
 * API service for distro preset operations
 */
const distroPresetAPI = {
  getAll: (campaignId) => fetch(`/api/campaign/${campaignId}/distropreset`),
  create: (campaignId, data) =>
    fetch(`/api/campaign/${campaignId}/distropreset`, {
      method: "POST",
      body: JSON.stringify(data),
      headers: { "Content-type": "application/json; charset=UTF-8" },
    }),
  update: (campaignId, id, data) =>
    fetch(`/api/campaign/${campaignId}/distropreset/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
      headers: { "Content-type": "application/json; charset=UTF-8" },
    }),
  delete: (campaignId, id) =>
    fetch(`/api/campaign/${campaignId}/distropreset/${id}`, {
      method: "DELETE",
      headers: { "Content-type": "application/json; charset=UTF-8" },
    }),
};

/**
 * DistroPreset Component
 * Manages distribution presets for fund allocation
 */
const Index = () => {
  const { campaign, setSpinner, initialDistroPreset } = useGlobal();
  const { apiConstants } = useWallet();
  // Initial form state
  const INITIAL_FORM_STATE = {
    id: 0,
    preset_name: "",
    campaign_id: 0,
    preset_order: 1,
    beneficiary_distro: initialDistroPreset.beneficiary_distro,
    sponsor_distro: initialDistroPreset.sponsor_distro,
    artist_distro: initialDistroPreset.artist_distro,
    seller_distro: initialDistroPreset.seller_distro,
    delete: false,
  };

  const [data, setData] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  const [formError, setFormError] = useState("");

  /**
   * Fetch distro preset data
   */
  useEffect(() => {
    const loadTableData = async () => {
      if (!campaign) return;

      setSpinner(true);
      try {
        const res = await distroPresetAPI.getAll(campaign.campaign_id);
        if (res.status === 200) {
          const json = await res.json();
          setData(json);
        }
      } catch (error) {
        console.error("Error loading distro presets:", error);
      } finally {
        setSpinner(false);
      }
    };

    loadTableData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [campaign]);

  /**
   * Show form for adding new preset
   */
  const handleAdd = () => {
    setShowForm(true);
    setFormData({
      ...INITIAL_FORM_STATE,
      preset_order: data.length + 1,
      ...(data[0] && {
        artist_distro: data[0].artist_distro,
        sponsor_distro: data[0].sponsor_distro,
        beneficiary_distro: data[0].beneficiary_distro,
        seller_distro: data[0].seller_distro,
      }),
    });
  };

  /**
   * Show form for editing preset
   */
  const handleEdit = (preset) => {
    setShowForm(true);
    setFormData({
      id: preset.id,
      preset_name: preset.preset_name,
      campaign_id: preset.campaign_id,
      preset_order: preset.preset_order,
      beneficiary_distro: preset.beneficiary_distro,
      sponsor_distro: preset.sponsor_distro,
      artist_distro: preset.artist_distro,
      seller_distro: preset.seller_distro,
      delete: false,
    });
  };

  /**
   * Show delete confirmation
   */
  const handleDelete = (preset) => {
    setShowForm(false);
    setFormData({
      id: preset.id,
      preset_name: preset.preset_name || "",
      delete: true,
    });
  };

  /**
   * Reset form and hide
   */
  const handleDiscard = () => {
    setShowForm(false);
    setFormData(INITIAL_FORM_STATE);
    setFormError("");
  };

  /**
   * Reload table data
   */
  const reloadData = async () => {
    try {
      const res = await distroPresetAPI.getAll(campaign.campaign_id);
      if (res.status === 200) {
        const json = await res.json();
        setData(json);
      }
    } catch (error) {
      console.error("Error reloading data:", error);
    }
  };

  /**
   * Create new distro preset
   */
  const handleCreate = async (formData) => {
    setFormError("");
    setSpinner(true);
    try {
      const res = await distroPresetAPI.create(campaign.campaign_id, {
        ...formData,
        campaign_id: campaign.campaign_id,
      });
      const response = await res.json();

      if (res.ok) {
        await reloadData();
        handleDiscard();
      } else {
        setFormError(response.error || "Failed to create preset");
      }
    } catch (error) {
      console.error("Error creating distro preset:", error);
      setFormError("An error occurred while creating the preset");
    } finally {
      setSpinner(false);
    }
  };

  /**
   * Update existing distro preset
   */
  const handleUpdate = async (formData) => {
    setFormError("");
    setSpinner(true);
    try {
      const res = await distroPresetAPI.update(campaign.campaign_id, formData.id, {
        ...formData,
        campaign_id: campaign.campaign_id,
      });
      const response = await res.json();

      if (res.ok) {
        await reloadData();
        handleDiscard();
      } else {
        setFormError(response.error || "Failed to update preset");
      }
    } catch (error) {
      console.error("Error updating distro preset:", error);
      setFormError("An error occurred while updating the preset");
    } finally {
      setSpinner(false);
    }
  };

  /**
   * Delete distro preset
   */
  const handleDeleteConfirm = async () => {
    setSpinner(true);
    try {
      const res = await distroPresetAPI.delete(campaign.campaign_id, formData.id);
      if (res.ok) {
        await reloadData();
      }
      handleDiscard();
    } catch (error) {
      console.error("Error deleting distro preset:", error);
    } finally {
      setSpinner(false);
    }
  };

  if (showForm) {
    return (
      <>
        <Title title="Distro Preset" />
        <Description text="Let your supporters choose how their funds are distributed by creating Distro Presets for them." />
        {formError && (
          <span className="form-text text-danger">{formError}</span>
        )}
        <FormCard>
          <Form
            handleSubmit={formData.id ? handleUpdate : handleCreate}
            formData={formData}
            onDiscard={handleDiscard}
            campaignDetails={campaign}
            distroPresetCount={data.length}
          />
        </FormCard>
      </>
    );
  }

  if (formData.delete && formData.id) {
    return (
      <Delete
        formData={formData}
        onDiscard={handleDiscard}
        handleSubmit={handleDeleteConfirm}
      />
    );
  }

  return (
    <>
      <Title title="Distro Preset" />
      <Description text="Let your supporters choose how their funds are distributed by creating Distro Presets for them." />
      {formError && (
        <span className="form-text text-danger">{formError}</span>
      )}

      {!campaign?.sc_id && (
        <div
          className={`add-btn ${data.length > MAX_PRESETS ? "disable_btn_add" : ""}`}
          onClick={data.length > MAX_PRESETS ? null : handleAdd}
        >
          Add Distro Preset
          <img src={plus} className="add-icon" alt="Add" />
        </div>
      )}

      <Table
        data={data}
        onEdit={handleEdit}
        onDelete={handleDelete}
        isAllowedToEditOrDelete={!campaign?.sc_id}
        reddevcut={apiConstants.reddevcut}
      />
    </>
  );
};

export default Index;
